import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileLines, faDownload, faLock } from '@fortawesome/free-solid-svg-icons';
import { CustomerButton } from './CustomerButton';
import { cn } from '@/lib/cn';

interface CustomerDocumentCardProps {
  /** e.g. "Quotation", "Work Order", "Inspection Report" */
  kind: string;
  /** File name as stored; null/undefined when no document is available. */
  fileName?: string | null;
  /** Reference number or secondary identifier shown above the file name. */
  reference?: string | null;
  /** Short facts rendered as a meta line (e.g. "PDF · Sent 5 Sep 2026"). */
  meta?: string[];
  /** Whether a downloadable file exists. */
  available: boolean;
  /** Copy shown when `available` is false. */
  unavailableText?: string;
  onDownload?: () => void;
  downloading?: boolean;
  downloadLabel?: string;
  /** Status chip / badge slot rendered top-right. */
  status?: ReactNode;
  /** Anything below the file row (notes, secondary info). */
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
  downloadTestId?: string;
}

/**
 * Premium document card — the customer's "thing to download". A PDF tile,
 * the reference and file name, a meta line, a status slot and one clear
 * download action that is full-width on phones and inline from `sm`.
 * The download itself is whatever the caller passes; this component never
 * builds links or touches the API.
 */
export function CustomerDocumentCard({
  kind,
  fileName,
  reference,
  meta = [],
  available,
  unavailableText = 'No document is available yet.',
  onDownload,
  downloading = false,
  downloadLabel = 'Download PDF',
  status,
  children,
  className = '',
  'data-testid': testId,
  downloadTestId,
}: CustomerDocumentCardProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'rounded-customer border border-customer-border bg-customer-surface-muted/60 p-3.5 sm:p-4',
        className,
      )}
    >
      <div className="flex items-start gap-3.5">
        <span
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-customer-sm',
            available
              ? 'bg-customer-danger/10 text-customer-danger-text'
              : 'bg-customer-text/[0.06] text-customer-text-secondary',
          )}
          aria-hidden="true"
        >
          <FontAwesomeIcon icon={available ? faFilePdf : faFileLines} className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-customer-text-secondary">
                {kind}
              </p>
              {reference && (
                <p className="mt-0.5 truncate text-sm font-semibold text-customer-text">
                  {reference}
                </p>
              )}
            </div>
            {status && <div className="shrink-0">{status}</div>}
          </div>

          {available ? (
            fileName && (
              <p className="mt-1 break-all text-sm text-customer-text/80" title={fileName}>
                {fileName}
              </p>
            )
          ) : (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-customer-text-secondary">
              <FontAwesomeIcon icon={faLock} className="h-3 w-3" aria-hidden="true" />
              {unavailableText}
            </p>
          )}

          {meta.length > 0 && (
            <p className="mt-1 text-xs text-customer-text-secondary">{meta.join(' · ')}</p>
          )}
        </div>
      </div>

      {available && onDownload && (
        <div className="mt-3 sm:flex sm:justify-end">
          <CustomerButton
            variant="outline"
            icon={faDownload}
            loading={downloading}
            loadingLabel="Downloading…"
            onClick={onDownload}
            data-testid={downloadTestId}
            className="w-full sm:w-auto"
          >
            {downloadLabel}
          </CustomerButton>
        </div>
      )}

      {children && <div className="mt-3 border-t border-customer-border pt-3">{children}</div>}
    </div>
  );
}
